import { APP_ROOT } from '@immediately-run/platform-constants';
import { throwOnRefusal } from './protocolRefusal';

import { useEffect, useState } from 'react';
import { protocolRequest, sendMessage, addListener } from './sandboxUtils';
import { createPushChannel } from './pushChannel';
import { getHostRuntime } from './hostRuntime';
import { mountMatches } from './mountMatch';
// R3-166 — the `spaces:*` family is GENERATED from the capability descriptor set
// (`scripts/codegen-prototype/descriptors.spaces.mjs`) rather than hand-written here.
// Re-exported from this module so every existing import path keeps working: the
// swap is a no-op to consumers (SDK_SIMPLIFICATION_SPEC §7 step 3), which is
// asserted by the emitted-`.d.ts` before/after comparison, not assumed. The five
// invite-inbox verbs joined the generated family with their `Invite` type (the
// declared "next migration increment" after #85); the mount verbs and the live
// inbox channel trio below stay hand-written — they are not request wrappers.
//
// The invite channel's parse below uses `Invite`, so it is imported (not only
// re-exported).
import type { Invite } from './generated/spaces';
export type { Role, SpaceInfo, Member, ResolvedUser, GrantRecord, Invite } from './generated/spaces';
export {
  listSpaces,
  listAllSpaces,
  getSpaceMembers,
  inviteToSpace,
  unshareSpace,
  setSpaceRole,
  lookupUser,
  listGrants,
  revokeGrant,
  listPendingInvites,
  revokeInvite,
  listMyInvites,
  acceptInvite,
  declineInvite,
  // R3-723 — the space lifecycle verbs (FILE_SHARING_SPEC §6.2/§9.7): soft
  // delete + undo, leave, rename, and the named one-way conversion.
  deleteSpace,
  restoreSpace,
  leaveSpace,
  renameSpace,
  convertSpaceToShared,
} from './generated/spaces';
export type {
  DeleteSpaceError,
  RestoreSpaceError,
  LeaveSpaceError,
  RenameSpaceError,
  ConvertSpaceToSharedError,
} from './generated/spaces';
// Type-only: `tasks.ts` registers a host listener at module load, so we reuse the
// FileCap SHAPE without pulling that side effect into every `mounts` importer.
import type { FileCap } from './tasks';
import {
  INVITATIONS,
  MOUNT_ADD,
  MOUNT_REMOVE,
  PROTOCOL_LOCALSTORE,
  PROTOCOL_SETTINGS,
  PROTOCOL_SPACES,
  REQUEST_INVITATIONS,
  REQUEST_MOUNTS,
  REQUEST_SESSION_MOUNTS,
  SESSION_MOUNTS,
} from './generated/protocol';
import { SCHEMES } from './protocolSchemes';
import { invoke } from './catalog';
// Re-exported here so an app needs ONE import for a federated mount's bundle facts
// (`SandboxMount.bundle` + the layout type it carries) — the same root-export
// convention `linkSpace.ts` applies to the mdx-plugins link types.
import type { BundleLayout } from '@immediately-run/mdx-plugins';
export type { BundleLayout };

/**
 * The absolute path where this app's own repository filesystem is mounted
 * (FILE_SHARING_SPEC §11.2). Prefer this over hardcoding `/app`: the repo is
 * dual-mounted at both `/app` (back-compat) and its canonical `/mnt/{hash}`
 * address, and this returns the canonical one the host reports. Falls back to
 * `/app` when the host hasn't reported a canonical path (older host / before the
 * report arrives) — both paths are live, so either resolves the same files.
 */
export const getAppMountPath = (): string => getHostRuntime()?.appMountPath ?? APP_ROOT;

/**
 * A filesystem mount available to the sandbox, mirrored from the host window.
 *
 * Mounts appear on demand — call {@link openSettings} for this app's own settings,
 * or {@link mountSpace} / {@link requestMount} to mount a Firestore-backed "space".
 * Read or subscribe to the set, then access the files through the `fs` module at
 * the mount's `path`.
 */
export interface SandboxMount {
  /** Absolute path where the mount is reachable (e.g. `/spaces/{id}`). */
  path: string;
  /** Backend kind, e.g. `'firestore'`. */
  type: string;
  /** Optional stable identifier (the spaceId, for spaces). */
  id?: string;
  /**
   * Access mode of the granted view: `'rw'` (read-write) or `'ro'` (read-only).
   * A live role downgrade re-announces the same mount with `mode: 'ro'`; apps
   * observing `onMountsChange` see the change and writes start failing `EROFS`.
   * Absent on the primary repo mount (treated as read-write).
   */
  mode?: 'ro' | 'rw';
  /**
   * Human-readable label for the mount — the space's display name, or the repo
   * label for the primary working-tree mount (R3-69). Use this to show users and
   * agents *what* a mount is: the `path` (`/mnt/{hash}`) and `id` (the spaceId)
   * are opaque, and space names are not unique, so neither alone tells you which
   * filesystem you're looking at. Absent when the host can't resolve a name
   * (older host, or a name it never learned) — fall back to `id`/`path`.
   */
  name?: string;
  /**
   * The granted scopes of this mount (plan 12 §8.7 / §F): each `{subtree, mode}`
   * is a path prefix you hold and at what access, at the mount's backend-natural
   * paths. Use it to reason about per-path writability — which subtree is `rw` —
   * WITHOUT probing `EROFS`. A single whole-mount grant is `[{ subtree: '/', mode }]`.
   * Absent on the primary repo mount and on an older host that doesn't report it.
   */
  rules?: MountRule[];
  /**
   * R3-546 (BUNDLE_EMBEDDING §4a.3, gate G-BE-19): bundle facts, when this mount is a
   * federated `bundle:` view over another bundle. The host parses the target bundle's
   * marker and hands the consumer its `kind` and its LAYOUT **pruned to this mount's
   * subtree view** — a record set, `tree` entry or `unique` reference the view cannot
   * reach is absent, so the description is never an existence map of what the chroot
   * answers `ENOENT` for. Pure data: nothing in it is authority, and the consumer
   * could have read it if it held the target's root. Absent on every non-bundle mount
   * and on older hosts.
   */
  bundle?: SandboxMountBundle;
  /**
   * R3-876 (APP_CUSTOMIZATION_SPEC §5a): the host's advisory reading of whether the
   * READER can edit this delegation's source — computed from the reader's own
   * authority (their role on the source space, or sign-in for a working tree), NOT
   * from anything about your own mount. Gate an Edit affordance on it
   * (`requestEdit({ bundleFile })` is the call it hints at): offer the control when
   * `true`, hide it when `false`, and treat an absent hint (older host, non-delegation
   * mount) as "unknown — offer and let the refusal tell you". Display-only: it is
   * never an authority input, and the host re-announces the mount when a role change
   * flips it.
   */
  readerCanEdit?: boolean;
}

/** The bundle facts a federated mount descriptor carries (§4a.3). */
export interface SandboxMountBundle {
  /** The target bundle's declared `kind`, when it declares one. */
  kind?: string;
  /**
   * R3-184 (PERSISTENCE_SPEC §8.0/§8.5) — the DECLARED in-bundle mount name
   * (`requests.mounts[].at`, e.g. `/items`): the alias the declaring bundle's own
   * documents address this view by, carried beside the mount's `path` (the
   * host-assigned task slot, §8.3). Pure data — it is never authority and never a
   * second mount point; a bundle-carrying app matches the alias back to the
   * declaration that minted the view. Absent on older hosts.
   */
  at?: string;
  /** The target's layout, pruned to this mount's view (`pruneLayoutToView`). */
  layout?: BundleLayout;
  /**
   * Why a refused or `forbidden` `bundle:` mount degraded the DECLARING bundle's
   * render (PERSISTENCE §8.0 containment: the carrying filesystem stays mounted).
   * Mount-resolution refusal codes (`escapes-filesystem`, `not-a-bundle`,
   * `unsafe-subtree`, `subtree-in-nested-bundle`, …), surfaced so the callee can
   * read them — the declaring bundle always renders, degraded wherever a
   * declaration did not resolve.
   */
  diagnostics?: readonly string[];
}

/** One granted scope of a mount (plan 12 §F): a backend-natural path prefix and
 *  the access mode there. The most specific (longest) matching rule governs a path. */
export interface MountRule {
  subtree: string;
  mode: 'ro' | 'rw';
}

/**
 * Why a mounted filesystem was removed, surfaced on the removed descriptor so an
 * app can say *why* it vanished instead of failing mutely (auth-mount §"mount-remove"
 * / AM2-4):
 * - `revoked` — a durable grant was revoked (revokeGrant / consent withdrawal);
 * - `unshared` — the granting user's membership was removed (or downgraded out);
 * - `signed-out` — sign-out tore down every mount;
 * - `unmounted` — the app's own `unmountSpace` (or region teardown);
 * - `deleted` — the space was soft-deleted.
 * An older host that sends no reason is read as `'revoked'` (most conservative).
 */
export type MountRemoveReason = 'revoked' | 'unshared' | 'signed-out' | 'unmounted' | 'deleted';

/** A descriptor delivered as REMOVED to a mounts-change listener: the mount that
 *  went away, plus the `reason` it did. */
export interface RemovedMount extends SandboxMount {
  reason: MountRemoveReason;
}

interface MountService {
  getMounts(): SandboxMount[];
  onChange(listener: (mounts: SandboxMount[], removed: RemovedMount[]) => void): { dispose(): void };
}

// The stable key of a mount: its `id` (spaceId) when present, else its `path`.
// Matches the sandbox `MountService.mountKey` so add/replace/remove agree on both
// sides of the wire (a role downgrade re-announces the SAME key with `mode: 'ro'`).
const mountKey = (m: SandboxMount): string => m.id ?? m.path;

const MOUNT_REMOVE_REASONS: ReadonlySet<string> = new Set<MountRemoveReason>([
  'revoked',
  'unshared',
  'signed-out',
  'unmounted',
  'deleted',
]);

// Normalize an over-the-wire `mount-remove` reason; an absent/unknown value (older
// host) reads as `'revoked'`, the most conservative reading (mirrors the sandbox).
const asMountRemoveReason = (value: unknown): MountRemoveReason =>
  typeof value === 'string' && MOUNT_REMOVE_REASONS.has(value) ? (value as MountRemoveReason) : 'revoked';

// The injected sandbox-bundler mount service (`module.evaluation.module.bundler.mounts`),
// or null when the SDK is npm-fetched with no injection — same dual-mode shape as
// `sandboxUtils.transport()` and the metadata emitter (SDK_PACKAGING_SPEC §4/§8).
/** @deprecated-path The injected `bundler.mounts` read — window opened 2026-08-25
 *  (R3-278). The protocol equivalent is `transportMountService()` below (the
 *  `mount-add`/`mount-remove` mirror + `request-mounts` replay), which the dual-mode
 *  chooser already falls back to. Injection stays preferred for byte-compat through
 *  the window; see DEPRECATION_CANDIDATES.md.
 */
const injectedMountService = (): MountService | null => {
  try {
    // @ts-ignore - injected by the sandbox runtime
    const svc = module?.evaluation?.module?.bundler?.mounts;
    return svc && typeof svc.getMounts === 'function' ? svc : null;
  } catch {
    return null;
  }
};

// Transport-backed descriptor cache (R3-51b): the npm-fetched fallback that builds
// the same `getMounts()`/`onChange()` view the injected `bundler.mounts` provides,
// directly from the host's `mount-add`/`mount-remove` messages over the §4 transport.
// The host already posts these (it's how the in-iframe bundler service is populated);
// the `MessagePort` a `mount-add` transfers is consumed by the sandbox runtime to wire
// ZenFS and is irrelevant here — the SDK only mirrors the *descriptors*. A lazy
// singleton so `getMounts`/`onMountsChange` share one cache, one subscription, and one
// `request-mounts` replay (the host re-announces every current mount, like a poll).
let transportSvc: MountService | null = null;

const transportMountService = (): MountService => {
  if (transportSvc) return transportSvc;
  let mounts: SandboxMount[] = [];
  const listeners = new Set<(m: SandboxMount[], r: RemovedMount[]) => void>();
  const fire = (removed: RemovedMount[]) => {
    for (const l of [...listeners]) l(mounts, removed);
  };

  addListener(MOUNT_ADD, (msg: Record<string, any>) => {
    const mount: SandboxMount | undefined = msg.mount;
    if (!mount) return;
    const key = mountKey(mount);
    mounts = [...mounts.filter((m) => mountKey(m) !== key), mount];
    fire([]);
  });
  addListener(MOUNT_REMOVE, (msg: Record<string, any>) => {
    const key: string | undefined = msg.id ?? msg.path;
    if (key == null) return;
    const reason = asMountRemoveReason(msg.reason);
    const removed = mounts.filter((m) => mountKey(m) === key).map((m) => ({ ...m, reason }));
    if (removed.length === 0) return;
    mounts = mounts.filter((m) => mountKey(m) !== key);
    fire(removed);
  });

  // Ask the host to replay the current set (the matching `mount-add`s may have been
  // sent before this SDK subscribed). Best-effort: a transport not yet ready throws.
  try {
    sendMessage(REQUEST_MOUNTS);
  } catch {
    /* transport not ready — the live mount-add stream still populates the cache */
  }

  transportSvc = {
    getMounts: () => mounts,
    onChange: (listener) => {
      listeners.add(listener);
      listener(mounts, []); // immediate replay to the new subscriber
      return { dispose: () => listeners.delete(listener) };
    },
  };
  return transportSvc;
};

// Phase-5 dual mode: prefer the injected bundler service (the live path, behaviour
// byte-for-byte unchanged); fall back to the transport-built cache when npm-fetched.
const mountService = (): MountService => injectedMountService() ?? transportMountService();

/** A predicate-style matcher for {@link findMount} / {@link waitForMount}. Any
 *  combination of coordinates; `name` matches the human-readable mount label. */
export type MountQuery = { type?: string; id?: string; path?: string; name?: string };

const matches = (mount: SandboxMount, query: MountQuery): boolean => mountMatches(mount, query);

/**
 * Returns the mounts currently available. Poll this whenever you need a one-off
 * read; use {@link onMountsChange} or {@link useMounts} to react to changes.
 * Each descriptor carries its `id` (the spaceId), `path` (`/mnt/{hash}`) and —
 * when the host can resolve it — a human-readable `name` (R3-69), so this doubles
 * as a queryable mount→space mapping for showing or locating a mount by name.
 */
export const getMounts = (): SandboxMount[] => mountService().getMounts();

/** Returns the first mount matching `query`, or `undefined`. */
export const findMount = (query: MountQuery): SandboxMount | undefined => getMounts().find((m) => matches(m, query));

/**
 * Subscribe to mount changes. The listener is invoked immediately with the
 * current mounts (and an empty `removed`), then again on every change. The second
 * argument carries the descriptors REMOVED by that change, each with its `reason`
 * (AM2-4) — so an app can react to *why* a mount vanished (e.g. tell the user a
 * shared space was `unshared` vs `deleted`). It is empty on adds and on the
 * initial replay. Returns an unsubscribe fn.
 */
export const onMountsChange = (listener: (mounts: SandboxMount[], removed: RemovedMount[]) => void): (() => void) => {
  const disposable = mountService().onChange(listener);
  return () => disposable.dispose();
};

/**
 * Resolves once a mount matching `query` is present (immediately if it already
 * is). Handy for "use it when it appears" — e.g.
 * `await waitForMount({ type: 'firestore' })` before reading `/firestore`.
 *
 * `timeoutMs` (optional, additive) rejects with a `timeout`-coded error instead of
 * waiting forever. Omit it to keep the original unbounded behaviour — but prefer
 * setting it on any path whose caller would otherwise hang silently: a mount that
 * never arrives is indistinguishable from one that is merely slow, and an awaited
 * promise that never settles surfaces to the user as a feature that quietly does
 * nothing.
 *
 * **Hazard — `onMountsChange` calls its listener SYNCHRONOUSLY on subscribe** (the
 * documented initial replay). So when the mount is already present — the common
 * case, since callers typically `await` the host request that creates it first —
 * the callback below runs *during* the `onMountsChange(...)` call, before the
 * assignment to `unsubscribe` completes. `unsubscribe` is therefore declared with
 * `let` ABOVE the subscription and read only inside a deferred closure: writing
 * `const unsubscribe = onMountsChange(...)` and referencing it in the callback
 * throws `ReferenceError: Cannot access 'unsubscribe' before initialization` (a
 * temporal-dead-zone read) on exactly that path. That bug silently broke
 * `openSettings()` — and with it the agent's conversation memory.
 */
export const waitForMount = (query: MountQuery, timeoutMs?: number): Promise<SandboxMount> =>
  awaitMatchingMount(onMountsChange, query, timeoutMs);

/** The framework-free core of {@link waitForMount}, with the subscription injected
 *  so a test can drive the synchronous-initial-replay case that broke it. */
export const awaitMatchingMount = (
  subscribe: (listener: (mounts: SandboxMount[]) => void) => () => void,
  query: MountQuery,
  timeoutMs?: number,
): Promise<SandboxMount> =>
  new Promise((resolve, reject) => {
    // `let`, declared BEFORE `subscribe(...)` — see the hazard note above. A
    // `const` bound to the subscribe call is in its temporal dead zone while the
    // synchronous initial replay runs, and any read of it from the listener
    // throws.
    let unsubscribe: (() => void) | undefined;
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Deferred so we never dispose the subscription from inside its own initial
    // replay, and late enough that `unsubscribe` is always assigned.
    const stop = (): void => {
      settled = true;
      if (timer !== undefined) clearTimeout(timer);
      void Promise.resolve().then(() => unsubscribe?.());
    };
    unsubscribe = subscribe((mounts) => {
      if (settled) return;
      const found = mounts.find((m) => matches(m, query));
      if (found) {
        stop();
        resolve(found);
      }
    });
    // The initial replay may have settled us above, before `unsubscribe` existed;
    // `stop()`'s deferred read picks it up, so nothing more is needed here.
    if (!settled && timeoutMs !== undefined) {
      timer = setTimeout(() => {
        if (settled) return;
        stop();
        const err = new Error(
          `waitForMount timed out after ${timeoutMs}ms waiting for ${JSON.stringify(query)}`,
        ) as SpaceError;
        err.code = 'timeout';
        reject(err);
      }, timeoutMs);
    }
  });

/** React hook returning the mounts currently available, re-rendering on change. */
export const useMounts = (): SandboxMount[] => {
  const [mounts, setMounts] = useState<SandboxMount[]>(getMounts);
  useEffect(() => onMountsChange(setMounts), []);
  return mounts;
};

// ---------------------------------------------------------------------------
// Session-scope mounts — the first-party "App | Session" lens (PRINCIPALS §9 B2).
// ---------------------------------------------------------------------------

/** A mount as seen through the first-party **Session** lens (PRINCIPALS_SPEC §9 B2):
 *  the session's mounts BEYOND this app's own (the editor/agent session's). This is
 *  a metadata view — no filesystem port — so it extends {@link SandboxMount} with only
 *  {@link forwardedToApp}. */
export interface SessionMount extends SandboxMount {
  /** True iff this mount is ALSO in the app's own {@link useMounts} (the App lens);
   *  `false` for a session-export-only mount visible only to the editor/agent + the
   *  Session lens. */
  forwardedToApp: boolean;
}

// The host pushes the session mount list ONLY to a FIRST-PARTY frame — the channel
// is gated by the first-party-only `mounts:registry` capability (§8.9.1 / D-PRIN-4).
// A URL-loaded/previewed app (or a fork of the File Explorer) never holds it, so the
// push never arrives and `initial: []` stands — the Session lens is simply absent,
// fail-closed. Mirrors the host's `session-mounts`/`request-session-mounts` wiring.
const sessionMountsChannel = createPushChannel<SessionMount[]>({
  pushType: SESSION_MOUNTS,
  requestType: REQUEST_SESSION_MOUNTS,
  initial: [],
  parse: (msg) => (Array.isArray(msg.mounts) ? (msg.mounts as SessionMount[]) : undefined),
});

/** The session's mounts (the "Session" lens superset), or `[]` when this frame is
 *  not first-party. One-off read; use {@link onSessionMountsChange}/{@link useSessionMounts}
 *  to react live. First-party only (`mounts:registry`) — a fork always sees `[]`. */
export const getSessionMounts = (): SessionMount[] => sessionMountsChannel.get();

/** Subscribe to Session-lens mount changes. Invoked immediately with the current
 *  list (`[]` for a non-first-party frame), then on every change. Returns an
 *  unsubscribe. */
export const onSessionMountsChange = (listener: (mounts: SessionMount[]) => void): (() => void) =>
  sessionMountsChannel.onChange(listener);

/** React hook returning the live "Session" lens mount list, re-rendering on change.
 *  Empty for any non-first-party frame (the host withholds the channel), so a URL-
 *  loaded File Explorer fork renders no Session lens. */
export const useSessionMounts = (): SessionMount[] => sessionMountsChannel.use();

// ---------------------------------------------------------------------------
// Spaces — on-demand, shareable Firestore-backed filesystems.
// The host owns all UX: if you aren't signed in, or the space doesn't exist or
// isn't accessible, the parent window presents sign-in / create / request-access
// and only then resolves these calls. See docs/specs/FILE_SHARING_SPEC.md.
// ---------------------------------------------------------------------------

/** An error from a space operation, carrying a machine-readable `code`.
 *
 *  **What you actually catch.** The SDK throws a plain `Error` with `.code` assigned — a
 *  `CodedRefusalError` from `protocolRefusal.ts` — never a distinct class. This interface
 *  documents the `.code` VALUES the host sends. Nothing enforces the union at runtime —
 *  `.code` is whatever string arrived — so treat an unlisted code as possible and
 *  `instanceof Error` as the only reliable test.
 */
export interface SpaceError extends Error {
  code:
    | 'auth-required'
    | 'cancelled'
    | 'forbidden'
    | 'not-found'
    | 'unsupported-scheme'
    // Client-side, never from the host: a bounded `waitForMount` gave up.
    | 'timeout'
    | 'unknown';
}

type SpaceResult = { ok: true; data: unknown } | { ok: false; code: string; message: string };

// Issue a spaces protocol request, unwrapping the host's {ok,data} envelope and throwing
// a coded refusal on failure — `throwOnRefusal` raises a `CodedRefusalError` whose `code`
// is whatever string the host sent, NOT a `SpaceError` (nothing enforces that union, and
// this helper no longer performs the throw). See `protocolRefusal.ts`.
const request = async <T = unknown>(method: string, query: Record<string, unknown> = {}): Promise<T> => {
  const res = (await protocolRequest(SCHEMES[PROTOCOL_SPACES], method, [query])) as SpaceResult;
  throwOnRefusal(res, 'space request failed');
  return res.data as T;
};

// Request a space mount, then wait until the host actually registers it. The
// host announces the mount (`mount-add`) separately from the protocol reply, so
// an immediate read could otherwise race the mount.
const requestMountInternal = async (method: string, query: Record<string, unknown>): Promise<SandboxMount> => {
  const mount = await request<SandboxMount>(method, query);
  return waitForMount({ id: mount.id ?? mount.path });
};

/**
 * Mount a filesystem by its **universal mount id** (UI_AS_APPS_SPEC §3.5) —
 * `scheme:locator`, e.g. `space:{spaceId}` or `github:owner/repo@ref`. Backend-blind:
 * the host resolves the scheme. A scheme with no resolver rejects with
 * {@link SpaceError} `unsupported-scheme`.
 */
export const mount = (mountId: string): Promise<SandboxMount> => requestMountInternal('mount', { mount: mountId });

/** Mount a specific space by id (e.g. one shared with you, or from a link). A thin
 *  shim over {@link mount} with the `space:` scheme. */
export const mountSpace = (query: { spaceId: string }): Promise<SandboxMount> => mount(`space:${query.spaceId}`);

/**
 * Ask the user to grant a filesystem to this app — the §8.6 powerbox. The app
 * asks; the HOST shows the user their spaces and, for the chosen one, its PROJECT
 * FOLDERS (§8.7). The user picks ONE project — so a shared space opens scoped to
 * just that project, never the whole space — and makes an EXPLICIT read-only vs
 * read-write decision (there is no default). The app never sees the list; it
 * resolves with the single granted mount, or rejects with a {@link SpaceError}
 * (`cancelled`) if declined. The granted scope is enforced host-side: the mount
 * is chroot'd to the project folder and `ro`-limited accordingly, so paths
 * outside the project are unnameable and writes on a `ro` grant fail `EROFS`.
 *
 * A project folder is the macOS-bundle-like unit an app works in inside a space;
 * the host records which app a folder belongs to (a `.immediately.run/` sidecar),
 * so the picker can surface the app's own projects or let the user create a new
 * one. Observe the granted access via {@link SandboxMount.mode}.
 *
 * Backend-general (§3.5): the picker offers whatever mounts the user has (today,
 * their spaces). Returns the granted mount by its universal id.
 */
export const requestMount = (): Promise<SandboxMount> => requestMountInternal('request', {});

/** Prompt the user to grant a mount, returning the granted {@link SandboxMount}.
 *  @deprecated renamed to {@link requestMount} (backend-general, §3.5). */
export const requestSpace = requestMount;

// ── content references (plan 12 §E / FILE_SHARING §7) ────────────────────────

/**
 * Build a persisted CONTENT REFERENCE to a file in a mount — a `{mountId, relPath}`
 * pointer your app serializes into ITS OWN content (a board's JSON, an MDX file's
 * frontmatter, an album manifest — the platform doesn't dictate the container) so a
 * later viewer can resolve it. It is exactly the §5.7 {@link capFile} shape: ONE
 * capability, two delivery modes — runtime delegation (a task param, authorized by
 * the caller) vs a durable reference (authorized per-viewer by {@link resolveContentRef}).
 * `relPath` is BACKEND-NATURAL, so the reference resolves to the SAME path for every
 * viewer. Cross-app/cross-project references default to `ro`.
 *
 *   const ref = makeContentRef({ mountId: 'space:ACME', relPath: 'office-seating/desk.mdx' }, { mode: 'ro' });
 *
 * The body repeats {@link capFile} rather than calling it, and that is deliberate:
 * `tasks.ts` registers a host listener at module load, so a VALUE import of it here
 * would run that side effect in every importer of `mounts` (which is why the
 * `FileCap` import above is type-only). The shape the two share is the spec's, and
 * the `FileCap` type is what holds them to it.
 */
export const makeContentRef = (ref: { mountId: string; relPath: string }, opts: { mode: 'ro' | 'rw' }): FileCap => ({
  $cap: 'file',
  mountId: ref.mountId,
  relPath: ref.relPath,
  mode: opts.mode,
});

/**
 * Resolve a content reference your app found in content it ALREADY holds
 * (FILE_SHARING §7 / UI_AS_APPS §8.7; "plan 12 §E"). This is a RELAY, not a
 * fabrication: the host honors it ONLY when your app
 * already holds a grant to `ref.mountId` (else `forbidden`) — apps follow
 * writer-authored links inside granted content; they cannot name a space from
 * nothing (T27). The host runs a per-VIEWER consent prompt (named via the owning
 * app's project sidecar), and existence is never leaked — a decline and a
 * non-existent path are indistinguishable.
 *
 * On allow, the host APPENDS a read scope for the referenced path to your grant
 * (durable; same §8.15 lifecycle) and returns the STABLE absolute `path` the file
 * is mounted at — identical for every viewer, so a path the author stored resolves
 * the same for you. Read it through the `fs` module at that path. Rejects with a
 * {@link SpaceError}: `forbidden` (you don't hold the referenced mount) or
 * `cancelled` (the viewer declined / the path doesn't exist — no oracle).
 *
 *   const { path } = await resolveContentRef(ref);
 *   const text = await fs.promises.readFile(path, 'utf8');
 */
export const resolveContentRef = async (ref: FileCap): Promise<{ path: string }> => {
  const path = await request<string>('resolveRef', { ref });
  return { path };
};

/**
 * Resolve a BATCH of content references in ONE consent round (FILE_SHARING §7 /
 * UI_AS_APPS §8.7; "plan 12 §E"). When a
 * board opens with several embedded references, pass them all here: the host
 * coalesces them into a SINGLE consent prompt listing every target, instead of one
 * prompt per reference. Same relay gate and per-viewer semantics as
 * {@link resolveContentRef} (each ref's mount must already be held), applied to the
 * whole set — it is all-or-nothing: the user allows the batch or declines it.
 *
 * Resolves `{ paths }` with the STABLE absolute path of each ref, in input order.
 * Rejects with a {@link SpaceError}: `forbidden` (a referenced mount isn't held) or
 * `cancelled` (the viewer declined).
 *
 *   const { paths } = await resolveContentRefs(board.references);
 */
export const resolveContentRefs = async (refs: FileCap[]): Promise<{ paths: string[] }> => {
  const paths = await request<string[]>('resolveRefs', { refs });
  return { paths };
};

// ---------------------------------------------------------------------------
// Settings — the per-user "~/.config"-style space (UI_AS_APPS_SPEC §3.3/§3.5/§8.2).
// Each app gets its OWN settings subdir, auto-provisioned and chroot'd by the host
// (no dialog, no powerbox). Read/write it through the returned mount's filesystem
// port — there is deliberately no key/value get/set API; settings are just files.
// ---------------------------------------------------------------------------

// Issue a `protocol-settings` request, unwrapping {ok,data} and throwing a coded refusal
// on failure (mirrors `request`; the thrown value is a `CodedRefusalError`, not a
// `SpaceError` — see `protocolRefusal.ts`).
const settingsRequest = async <T = unknown>(method: string, query: Record<string, unknown> = {}): Promise<T> => {
  const res = (await protocolRequest(SCHEMES[PROTOCOL_SETTINGS], method, [query])) as SpaceResult;
  throwOnRefusal(res, 'settings request failed');
  return res.data as T;
};

/**
 * Mount this app's per-user settings — a private `~/.config`-style filesystem,
 * auto-provisioned for the signed-in user and isolated to THIS app (the host
 * chroots it; a different app can never name it). Read/write config files through
 * the returned mount. Rejects with a {@link SpaceError} (`auth-required`) when
 * signed out. Capability: baseline `settings:app`.
 *
 * **Which filesystem you get, and when that can change** (R3-413): for an
 * ordinary app — including one holding space grants, powerbox-picked or
 * declared — this is the app-level store (same mount id every call, so "which
 * space did I pick" style state survives later grants; no need to open early and
 * keep the handle). An instance the host has **floored** below its app tier (the
 * generic-viewer containment, `TRUST_MODES_SPEC` §5) keeps that app-level store
 * unless exactly one origin it opened at runtime is less trusted than the app; in
 * that one case it gets a per-origin partition instead — a DIFFERENT filesystem,
 * chosen by the host, that changes when the loaded origin changes. That
 * partition is normally writable: it is tiered by that origin's own trust.
 *
 * **A floored instance's settings mount may open read-only; the call still
 * resolves.** A floor below the app's tier can make the store you get read-only,
 * and that is most often the app's own app-level store above, not the per-origin
 * partition. An ordinary app that holds a space shared more widely than itself is
 * floored this way, so "my app is ordinary" does not mean "my settings mount is
 * writable".
 *
 * The open is not refused when writing is forbidden. You get a mount whose
 * descriptor is `mode: 'ro'`; any write through it fails with `EROFS`; and the
 * host shows its own notice explaining why. So check `mount.mode` on resolution
 * rather than treating this call as always-writable. A refloor mid-session ends
 * the app's realm and restarts it, so checking on each resolution covers that too.
 * Never surface an `EROFS` from a write as a fatal error: the app can still read,
 * and the remedy is the user's — revoking the widely-shared space in Settings →
 * App permissions.
 *
 * Which partition you get is still host-chosen and still changes with the loaded
 * origin. If your app can run floored and needs continuity across origins, keep
 * state per-mount (the returned `SandboxMount.id` tells you which partition you
 * are in).
 */
export const openSettings = async (): Promise<SandboxMount> => {
  const mount = await settingsRequest<SandboxMount>('open');
  // The host has already accepted the request and announced the mount, so this
  // normally resolves on the initial replay. Bounded anyway: an unbounded wait
  // here turns any delivery failure into a promise that never settles, and every
  // caller of `openSettings()` is doing it to reach durable state — so the app
  // just quietly loses that state with nothing to report.
  return waitForMount({ id: mount.id ?? mount.path }, SETTINGS_MOUNT_TIMEOUT_MS);
};

/** How long `openSettings()` waits for the host to deliver the mount it just
 *  agreed to create. Generous — this is a hang-breaker, not a latency budget. */
const SETTINGS_MOUNT_TIMEOUT_MS = 15_000;

/**
 * One-time SEED of this app's settings from the parent it declares as `forkOf`
 * (its `package.json` `immediately.run.forkOf`) — so a fork inherits your
 * preferences from the original app (UI_AS_APPS_SPEC §3.4). The host asks the user
 * to confirm (a full consent when the apps have different owners, a light confirm
 * when the same owner publishes both) and copies the parent's settings into this
 * app's own subdir, skipping any file you already have. Non-throwing: resolves
 * `{ ok:false, code }` on decline (`cancelled`), no declared parent (`forbidden`),
 * or signed-out (`auth-required`). After `{ ok:true }`, read {@link openSettings}.
 * Capability: baseline `settings:fork`.
 */
export const importSettingsFromParent = async (): Promise<
  { ok: true; copied: number } | { ok: false; code: string }
> => {
  try {
    const data = await settingsRequest<{ copied: number }>('importFromParent');
    return { ok: true, copied: data.copied };
  } catch (e) {
    return { ok: false, code: (e as SpaceError).code ?? 'unknown' };
  }
};

/**
 * Mount ANOTHER app's per-user settings by its `appKey` — the elevated "file
 * commander" surface. Rejects `forbidden` unless this app holds the first-party-
 * only `settings:all` capability. Most apps want {@link openSettings} instead.
 */
export const openSettingsOf = async (appKey: string): Promise<SandboxMount> => {
  const mount = await settingsRequest<SandboxMount>('openOf', { appKey });
  return waitForMount({ id: mount.id ?? mount.path });
};

/**
 * List every app that has per-user settings — the elevated "file commander"
 * enumeration. Pair with {@link openSettingsOf} to mount any of them. Rejects
 * `forbidden` unless this app holds the first-party-only `settings:all`.
 */
export const listSettingsApps = (): Promise<string[]> => settingsRequest<string[]>('list');

// ---------------------------------------------------------------------------
// Device-local store — the per-app, device-local, IndexedDB-backed filesystem
// (FILESYSTEM_SPEC §2.8). The distinction from `settings:` is durability, stated
// to the app via the mount's `type: 'localstore'`, never a guess.
// ---------------------------------------------------------------------------

// Issue a `protocol-localstore` request, unwrapping {ok,data} and throwing a coded refusal
// on failure (mirrors `settingsRequest`; a `CodedRefusalError`, not a `SpaceError`).
const localStoreRequest = async <T = unknown>(method: string, query: Record<string, unknown> = {}): Promise<T> => {
  const res = (await protocolRequest(SCHEMES[PROTOCOL_LOCALSTORE], method, [query])) as SpaceResult;
  throwOnRefusal(res, 'localstore request failed');
  return res.data as T;
};

/**
 * Mount this app's **device-local** store — a private, per-device, IndexedDB-backed
 * filesystem, NOT synced (unlike {@link openSettings}). The cheap, throwaway-able
 * home for a cache, a draft, a scroll position, a checkpoint; the wrong home for
 * anything the user would mourn (the browser may evict it under storage pressure,
 * and the docs say so). Read/write it through `fs` at the returned mount's `path`,
 * with `type: 'localstore'`.
 *
 * Isolation is the store itself, keyed on the app's `appKey` — an app can never
 * name another app's local store, there is deliberately no `openOf` sibling, and
 * nothing written here is synced. Capability: baseline `storage:local` (no consent
 * prompt). Rejects with a {@link SpaceError}: `auth-required` when signed out,
 * `forbidden` when the capability is absent. A quota/external failure surfaces the
 * underlying error unchanged, so an app can tell "the browser evicted me" from
 * "I never wrote anything".
 *
 * **A floored instance's local store may open read-only; the call still
 * resolves.** The same degrade {@link openSettings} documents applies here: when
 * the host has floored the instance below its app tier, the store may be announced
 * `mode: 'ro'`, writes through it fail `EROFS`, and the host shows its own notice.
 * Check the mount's `mode` as you would a settings mount, and never surface that
 * `EROFS` as fatal — but do not treat a failed write as harmless: this store holds
 * drafts and checkpoints too, so tell the user their save did not happen rather
 * than swallowing it.
 */
export const openLocalStore = async (): Promise<SandboxMount> => {
  const mount = await localStoreRequest<SandboxMount>('open');
  // The host has already accepted the request and announced the mount, so this
  // normally resolves on the initial replay. Bounded anyway, for the same reason
  // `openSettings()` bounds its wait: an unbounded wait turns any delivery failure
  // into a promise that never settles, and the caller reached for durable state.
  return waitForMount({ id: mount.id ?? mount.path }, LOCALSTORE_MOUNT_TIMEOUT_MS);
};

/** How long `openLocalStore()` waits for the host to deliver the mount it just
 *  agreed to create. Generous — this is a hang-breaker, not a latency budget. */
const LOCALSTORE_MOUNT_TIMEOUT_MS = 15_000;

/** Create a brand-new, empty platform-hosted space, granted to THIS app in full
 *  (read-write) — the user's create consent is consent for the app to create
 *  storage for itself, and the host records the same durable grant the
 *  {@link requestMount} powerbox would. So the returned mount can be re-opened
 *  later with {@link mountSpace} / {@link mount} (`space:<id>`) with no prompt —
 *  on the next load, in another tab, after sign-out/sign-in — until the user
 *  revokes the grant in their grants surface, after which `mount` answers
 *  `forbidden`. Other apps get nothing: they still reach the space only through
 *  the powerbox. (Before site-main R3-406 no grant was recorded and the space
 *  could only be re-found via the powerbox.) */
export const createSpace = (opts: { name?: string } = {}): Promise<SandboxMount> =>
  requestMountInternal('create', opts);

/** Release a mounted space (stops its listener on the host). */
export const unmountSpace = async (query: { spaceId: string }): Promise<void> => {
  await request('unmount', query);
};

// ---------------------------------------------------------------------------
// Space management (the space-manager app) — UI_AS_APPS_SPEC §5.2. These are
// ELEVATED: enumerating all the user's spaces is `spaces:user`; mutating
// membership (share/unshare/setRole) and resolving handles is `spaces:admin`.
// The host enforces the owner-lockout invariant (a space always keeps an owner,
// T41) and rate-limits handle lookups (L1); the OAuth/identity token never
// crosses to the app.
//
// The request verbs above (invite inbox included) are generated from the
// descriptor set; what remains hand-written here is the live invitations
// channel (FILE_SHARING §6.4/§9.8): the host pushes the caller's current
// invitations on change and replays on register-frame; gated `spaces:user`.
// So an invite that arrives (or an accepted/declined one leaving) reflects
// within one snapshot — no poll. Mirrors the host's
// `invitations`/`request-invitations` wiring.
const invitesChannel = createPushChannel<Invite[]>({
  pushType: INVITATIONS,
  requestType: REQUEST_INVITATIONS,
  initial: [],
  parse: (msg) => (Array.isArray(msg.invites) ? (msg.invites as Invite[]) : undefined),
});

/** The caller's current invitations (`spaces:user`). One-off read; use
 *  {@link onInvitesChange}/{@link useInvites} to react live. */
export const getInvites = (): Invite[] => invitesChannel.get();

/** Subscribe to invitation-inbox changes (arrived / accepted / declined). Invoked
 *  immediately with the current list, then on every change. Returns an unsubscribe. */
export const onInvitesChange = (listener: (invites: Invite[]) => void): (() => void) =>
  invitesChannel.onChange(listener);

/** React hook returning the caller's live invitation inbox, re-rendering on change
 *  (the space-manager Invitations inbox, §9.8). */
export const useInvites = (): Invite[] => invitesChannel.use();

// ---------------------------------------------------------------------------
// The space keyring (REALTIME_MESSAGING_SPEC §6.1, R3-633f). Hand-written on
// purpose, and NOT moved into the generated family: the generated wrappers
// return the wire payload as typed, while this boundary must PROJECT — a host
// reply that (wrongly) carried a private half (a `d` field) is never
// propagated, because the type is the boundary and the projection is the
// proof (§16's must-establish row: "a member's space private key is never
// returned to an app sandbox"). Both calls are `spaces:user` (§6.5(d): public
// halves only — no new capability).

/** One member's published space key — the PUBLIC half only. The ECDH P-256
 *  public JWK carries exactly `kty`/`crv`/`x`/`y`; there is no field for the
 *  private scalar anywhere in this type (and none named `d`), so the shape
 *  cannot carry one. */
export interface PublishedSpaceKey {
  /** The member the key belongs to. */
  uid: string;
  /** The key id (write-once; rotation appends a new kid). */
  kid: string;
  /** The public JWK (four fields, no `d`). */
  pub: { kty: string; crv: string; x: string; y: string };
  /** The algorithm label (see the host's SPACE_KEY_ALG_LABEL). */
  alg: string;
  /** Epoch milliseconds when the key was published. */
  addedAt: number;
}

/** Publish the caller's own space key (REALTIME_MESSAGING §6.1) — minted at join
 *  time by the host already; this call is the explicit retry/self-heal an app can
 *  offer. Idempotent: a member who has published resolves the existing kid with
 *  `created: false`. The private half is sealed under the member's passkey-derived
 *  KEK host-side and never crosses this boundary. Rejects with a typed refusal:
 *  `cancelled` (the passkey prompt was dismissed), `unsupported` (the platform
 *  cannot seal here), `forbidden` (not a member), `auth-required` when signed out. */
export const publishSpaceKey = async (spaceId: string): Promise<{ kid: string; created: boolean }> => {
  // `invoke`, not the local `request` helper: the wire-shape gate
  // (check-protocol-snapshot) extracts the catalog front door's call sites, and
  // a helper call is invisible to it — the published contract and this source
  // must agree byte-for-byte. Same envelope unwrap + coded refusal, with one
  // recorded difference: `request`'s throwOnRefusal coerces a NON-STRING refusal
  // code/message to 'unknown'/a fallback, while catalog.ts's invoke assigns
  // `res?.code ?? 'unknown'` unchecked (its pre-fold shape — protocolRefusal.ts's
  // census). The wire sends strings; the difference is unreachable today.
  const r = await invoke<{ ok: true; kid: string; created: boolean }>('spaces:publishSpaceKey', { spaceId });
  return { kid: r.kid, created: r.created };
};

/** The published public halves of a space's members (REALTIME_MESSAGING §6.1) —
 *  readable by every member, refused `forbidden` for a non-member (the store read
 *  is the membership gate). Members who have not published are simply ABSENT
 *  (§6.2's fail-closed create reads that absence; the targeted probe is the
 *  optional `uid` form, which answers `not-found` for an unpublished member).
 *  The returned rows are PROJECTED to {@link PublishedSpaceKey}: a wire row
 *  carrying anything more (e.g. a `d` field) is not propagated. */
export const listSpaceKeys = async (spaceId: string, uid?: string): Promise<PublishedSpaceKey[]> => {
  // One declared params type (never a conditional literal) so the wire-shape
  // gate fingerprints `uid` as `string | undefined` — the published snapshot's
  // recorded shape (sandbox-protocol 0.15.0). `invoke` here for the same reason
  // as publishSpaceKey above (the gate extracts the catalog front door's call
  // sites); the same invoke-vs-throwOnRefusal coercion note applies.
  const params: { spaceId: string; uid?: string } = { spaceId };
  if (uid !== undefined) params.uid = uid;
  const rows = await invoke<Array<Record<string, unknown>>>('spaces:listSpaceKeys', params);
  // Project, never coerce (the host store's own doctrine): the four public JWK
  // fields are copied verbatim and EVERYTHING else is dropped — a row carrying a
  // private half (`d`) does not propagate; a malformed row is the host's to
  // reject upstream (its project() refuses one), never this layer's to repair.
  return rows.map((row) => {
    const pub = row.pub as PublishedSpaceKey['pub'];
    return {
      uid: row.uid as string,
      kid: row.kid as string,
      pub: { kty: pub.kty, crv: pub.crv, x: pub.x, y: pub.y },
      alg: row.alg as string,
      addedAt: row.addedAt as number,
    };
  });
};
